import CredentialFormBase from "./credential_form_base"

// Dedicated to the "Connect a custom API" dialog (_custom_modal.html.erb) — a plain-language
// two-question form (where does the API live, how does it check who you are) instead of the
// generic modal's raw auth_type/source_type dropdowns and every provider's fields at once.
export default class extends CredentialFormBase {
  static targets = [
    "authType", "authTile",
    "apiKeyFields", "bearerTokenFields", "basicAuthFields", "oauthFields",
    "grantTypeSelect", "passwordGrantFields", "authCodeOnlyFields",
    "connectButton", "saveButton", "legacyRefresh", "validationMessage"
  ]

  connect() {
    this.updateAuth() // also runs updateGrantType()
    this.openDialog()
  }

  selectAuth(event) {
    this.authTypeTarget.value = event.currentTarget.dataset.authType
    this.authTileTargets.forEach(tile => this.markChoice(tile, tile === event.currentTarget))
    this.updateAuth()
  }

  updateAuth() {
    this.clearValidation()
    const auth = this.authTypeTarget.value
    for (const [name, targets] of Object.entries({
      "API Key": this.apiKeyFieldsTargets, "Bearer Token": this.bearerTokenFieldsTargets,
      "Basic Auth": this.basicAuthFieldsTargets, "OAuth 2.0": this.oauthFieldsTargets
    })) {
      targets.forEach(section => this.toggleSection(section, auth === name))
    }
    if (this.hasLegacyRefreshTarget) this.toggleSection(this.legacyRefreshTarget, auth !== "OAuth 2.0")
    // Save and connect only ever applies to OAuth authorization_code, so switching away from
    // OAuth (or between grant types) has to re-check it too, not just on initial connect().
    this.updateGrantType()
  }

  updateGrantType() {
    const oauth = this.authTypeTarget.value === "OAuth 2.0"
    const grant = this.hasGrantTypeSelectTarget ? this.grantTypeSelectTarget.value : null
    const authorize = oauth && grant === "authorization_code"
    this.passwordGrantFieldsTargets.forEach(el => this.toggleSection(el, oauth && grant === "password"))
    this.authCodeOnlyFieldsTargets.forEach(el => this.toggleSection(el, oauth && grant === "authorization_code"))
    if (this.hasConnectButtonTarget) this.connectButtonTarget.classList.toggle("hidden", !authorize)
    if (this.hasSaveButtonTarget) {
      for (const name of ["bg-indigo-600", "text-white", "hover:bg-indigo-700"]) this.saveButtonTarget.classList.toggle(name, !authorize)
      for (const name of ["text-indigo-700", "hover:bg-indigo-50"]) this.saveButtonTarget.classList.toggle(name, authorize)
    }
  }

  validate(event) {
    this.clearValidation()
    const auth = this.authTypeTarget.value
    let required = [this.element.querySelector('[name="api_credential[base_uri]"]')]
    if (auth === "OAuth 2.0") {
      const keys = ["oauth_client_id", "oauth_token_url"]
      if (["body", "basic"].includes(this.credentialInput("oauth_client_auth_method")?.value)) keys.push("oauth_client_secret")
      if (this.hasGrantTypeSelectTarget && this.grantTypeSelectTarget.value === "authorization_code") keys.push("oauth_authorize_url")
      if (this.hasGrantTypeSelectTarget && this.grantTypeSelectTarget.value === "password") keys.push("oauth_username", "oauth_password")
      required.push(...keys.map(key => this.credentialInput(key)).filter(Boolean))
    } else {
      required.push(...this.element.querySelectorAll('[name^="credentials["]:not([type="hidden"]):not(:disabled)'))
    }
    const missing = required.filter(input => input && !input.value.trim())
    missing.forEach(input => this.highlightError(input, "This field is required."))
    if (missing.length) {
      event.preventDefault()
      this.validationMessageTarget.textContent = "Complete the highlighted fields to save your connection."
      this.validationMessageTarget.classList.remove("hidden")
      const first = missing[0]
      first.closest("details")?.setAttribute("open", "")
      if (first.closest(".hidden")) this.validationMessageTarget.focus()
      else first.focus()
    }
  }

  clearValidation() {
    this.element.querySelectorAll('[aria-invalid="true"]').forEach(input => {
      input.removeAttribute("aria-invalid")
      input.classList.remove("border-red-500")
      this.element.querySelector(`#${input.id}-error`)?.remove()
      input.removeAttribute("aria-describedby")
    })
    this.validationMessageTarget.classList.add("hidden")
  }

  highlightError(input, message) {
    input.setAttribute("aria-invalid", "true")
    input.classList.add("border-red-500")
    const error = document.createElement("p")
    error.id = `${input.id}-error`
    error.className = "mt-1 text-sm text-red-700"
    error.textContent = message
    input.setAttribute("aria-describedby", error.id)
    const wrapper = input.closest('[data-controller="password-visibility"]') || input
    wrapper.after(error)
  }
}
